<template>
  <div class="record-modal">
    <div ref="kpiRecordEditModal" class="modal fade" data-bs-backdrop="static" id="kpiRecordEditModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" v-draggable-modal>
        <div class="modal-content bg-primary">
          <div class="modal-header">
            <div class="h5 modal-title text-light">{{ heading }} {{ kpi.name }} [{{ kpi.unit }}]</div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body bg-light">
            <div v-if="(kpiRecordMode === 'plan' || kpiRecordMode === 'objective') && this.kpi.timeDimInput !== 'Y'" class="row col-6">
              <div class="col text-start">
                <label for="kpiYearValue" class="form-label">{{ $t("views.kpi.edit.yearValue") }}</label>
              </div>
              <div class="col text-start">
                <input v-model="form.yearValue.content" type="text" id="kpiYearValue" @blur="this.formatInputNumber(form.yearValue.content)" @change="distributeYearValue(form.yearValue.content, kpiRecordMode)" class="form-control form-control-sm mb-3" :disabled="planningCompleted">
              </div>
          </div>

          <div v-if="planningCompleted" class="alert alert-warning p-1"> {{ $t("views.kpi.edit.warningPlanningCompleted") }} </div>

            <table class="table table-sm mb-0">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="text-start">{{ $t("views.kpi.edit.timeDim") }}</th>
                  <th v-if="['objective', 'plan'].includes(kpiRecordMode)" scope="col">{{ $t("views.kpi.edit.plan") }}</th>
                  <th v-if="['actual'].includes(kpiRecordMode)" scope="col">{{ $t("views.kpi.edit.actual") }}</th>
                  <th v-if="['objective'].includes(kpiRecordMode)" scope="col">{{ $t("views.kpi.edit.objective") }}</th>
                  <th v-if="['actual', 'plan'].includes(kpiRecordMode)" scope="col">{{ $t("views.kpi.edit.forecast") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="periodIndex in selectedKPIRecords.order" :key="periodIndex" >
                  <td><input :value="formatPeriod(selectedKPIRecords[periodIndex].period)" type="text" class="form-control form-control-sm" disabled></td>
                  <td v-if="kpiRecordMode !== 'actual'">
                    <input @input="calcValues('plan', periodIndex)" @change="mirrorForecast(periodIndex)" v-model="selectedKPIRecords[periodIndex].plan" type="text" @blur="focusOut('plan', selectedKPIRecords[periodIndex].plan, periodIndex)" class="form-control form-control-sm text-end" :disabled="selectedKPIRecords[periodIndex].disabled || kpiRecordMode === 'objective' || planningCompleted" >
                  </td>
                  <td v-if="kpiRecordMode === 'plan'">
                    <input :value="selectedKPIRecords[periodIndex].forecast" type="text" class="form-control form-control-sm text-end" disabled>
                  </td>
                  <td v-if="kpiRecordMode === 'actual'">
                    <input @input="calcValues('actual', periodIndex)" v-model="selectedKPIRecords[periodIndex].actual" type="text" @blur="focusOut('actual', selectedKPIRecords[periodIndex].actual, periodIndex)" class="form-control form-control-sm text-end" :disabled="selectedKPIRecords[periodIndex].disabled">
                  </td>
                  <td v-if="kpiRecordMode === 'objective'">
                    <input @input="calcValues('objective', periodIndex)" v-model="selectedKPIRecords[periodIndex].objective" type="text" @blur="focusOut('objective', selectedKPIRecords[periodIndex].objective, periodIndex)" class="form-control form-control-sm text-end" :disabled="selectedKPIRecords[periodIndex].disabled">
                  </td>
                  <td v-if="kpiRecordMode === 'actual'">
                    <input @input="calcValues('forecast', periodIndex)" v-model="selectedKPIRecords[periodIndex].forecast" type="text" @blur="focusOut('forecast', selectedKPIRecords[periodIndex].forecast, periodIndex)" class="form-control form-control-sm text-end" :disabled="selectedKPIRecords[periodIndex].disabled || selectedKPIRecords[periodIndex].actual !== ''">
                  </td>
                </tr>
              </tbody>
            </table>
            <div>
              {{ $t("views.kpi.edit.aggregation") }}:
              <span v-if="this.kpi.aggregation === 'S'"> {{ $t("views.kpi.edit.sum") }} </span>
              <span v-if="this.kpi.aggregation === 'M'"> {{ $t("views.kpi.edit.mean") }} </span>
              <span v-if="this.kpi.aggregation === 'A'"> {{ $t("views.kpi.edit.ultimate") }} </span>
            </div>
          </div>
          <div class="modal-footer bg-light justify-content-end">
            <div>
              <button type="button" class="btn btn-sm btn-outline-secondary me-2" data-bs-dismiss="modal">{{ $t("general.close") }}</button>
              <button @click="save()" type="button" class="btn btn-sm btn-primary" :disabled="planningCompleted">{{ $t("general.save") }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as formatUtils from '../../composables/formatUtils'
import { Modal } from 'bootstrap'
import { ref } from 'vue'

export default {
  name: 'KPIRecordEditModal',
  data () {
    return {
      form: {
        yearValue: {
          content: '',
          error: ''
        }
      }
    }
  },
  props: ['kpi'],
  computed: {
    selectedKPI () {
      return this.$store.state.selectedKPI
    },
    selectedKPIRecords () {
      return this.$store.state.selectedKPIRecords
    },
    kpiRecordMode () {
      return this.$store.state.kpiRecordMode
    },
    selectedYear () {
      return this.$parent.selectedYear
    },
    heading () {
      return this.$parent.heading
    },
    planningCompleted () {
      if (this.kpiRecordMode !== 'plan') return false
      if (!this.selectedKPIRecords) return false
      if (!Array.isArray(this.selectedKPIRecords.order)) return false
      if (this.selectedKPIRecords.order.length === 0) return false

      return this.selectedKPIRecords.order.some(
        periodIndex => this.selectedKPIRecords[periodIndex].actual !== ''
      )
    }
  },
  methods: {
    distributeYearValue: function (number) {
      const mode = this.kpiRecordMode
      if (number !== '') {
        number = this.unFormatNumber(number)
        // invalid year number value
        if (Number.isNaN(number)) {
          this.form.yearValue.content = ''
          return
        }

        const year = this.selectedYear
        const monthQuarterYear = this.kpi.timeDimInput
        const decimals = this.kpi.decimals
        let value = 0
        let period = 0
        let monthValue = 0
        let quarterValue = 0

        // with accumulated or mean, every month/quarter get the same value
        if (this.kpi.aggregation === 'A' || this.kpi.aggregation === 'M') {
          if (monthQuarterYear === 'M') {
            for (let i = 1; i < 13; i++) {
              period = year.toString() + i.toString().padStart(2, '0')
              this.selectedKPIRecords[period][mode] = this.formatNumber(number)
              this.mirrorForecast(period)
            }
          }
          for (let i = 1; i < 5; i++) {
            period = year.toString() + i.toString()
            this.selectedKPIRecords[period][mode] = this.formatNumber(number)
            this.mirrorForecast(period)
          }
          period = year.toString()
          this.selectedKPIRecords[period][mode] = this.formatNumber(number)
          this.mirrorForecast(period)
        }

        // with sum, every month/quarter must be calculated, the last month/quarter will be adjusted for correct year values
        if (this.kpi.aggregation === 'S') {
          if (monthQuarterYear === 'M') {
            monthValue = Number.parseFloat(number / 12).toFixed(decimals)
            for (let i = 1; i < 13; i++) {
              period = year.toString() + i.toString().padStart(2, '0')
              if (i !== 12) {
                value = monthValue
              } else {
                value = number - (monthValue * 11)
              }
              this.selectedKPIRecords[period][mode] = this.formatNumber(value)
              this.mirrorForecast(period)
            }
            for (let i = 1; i < 5; i++) {
              period = year.toString() + i.toString()
              if (i !== 4) {
                value = monthValue * 3
              } else {
                value = (monthValue * 2) + (number - (monthValue * 11))
              }
              this.selectedKPIRecords[period][mode] = this.formatNumber(value)
            }
            period = year.toString()
            this.selectedKPIRecords[period][mode] = this.formatNumber(number)
          }
          if (monthQuarterYear === 'Q') {
            quarterValue = Number.parseFloat(number / 4).toFixed(decimals)
            for (let i = 1; i < 5; i++) {
              period = year.toString() + i.toString()
              if (i !== 4) {
                value = quarterValue
              } else {
                value = (number - (quarterValue * 3))
              }
              period = year.toString() + i.toString()
              this.selectedKPIRecords[period][mode] = this.formatNumber(value)
              this.mirrorForecast(period)
            }
          }
          period = year.toString()
          this.selectedKPIRecords[period][mode] = this.formatNumber(number)
        }
        this.form.yearValue.content = ''
      }
    },

    focusOut: function (mode, number, periodIndex) {
      const formattedNumber = this.formatInputNumber(number)
      this.selectedKPIRecords[periodIndex][mode] = formattedNumber
    },
    async save () {
      this.$store.commit('SET_LOADING_STATUS', true)
      const elementData = { id: this.kpi.id, records: this.selectedKPIRecords, selectedYear: this.selectedYear }
      await this.$store.dispatch('updateKPIRecords', elementData).then(() => {
        this.closeModal()
      })

      this.$store.commit('SET_LOADING_STATUS', false)
    },
    mirrorForecast (periodIndex) {
      if (!this.selectedKPIRecords[periodIndex].actual) {
        this.selectedKPIRecords[periodIndex].forecast = this.formatNumber(this.unFormatNumber(this.selectedKPIRecords[periodIndex].plan))
        this.calcForecastValues(periodIndex.substring(0, 4))
      }
    },

    calcForecastValues (period) {
      let periodIndex = ''
      let quarterValue = 0
      let quarterCount = 0
      let quarterLast = null

      let yearValue = 0
      let yearCount = 0
      let yearLast = null

      const addValue = (raw, isQuarter) => {
        if (raw === '' || raw === null || raw === undefined) return

        const num = parseFloat(this.unFormatNumber(raw))
        if (isNaN(num)) return

        if (isQuarter) {
          quarterValue += num
          quarterCount++
          quarterLast = num
        }

        yearValue += num
        yearCount++
        yearLast = num
      }

      // -----------------------------
      // MONTHLY INPUT (M)
      // -----------------------------
      if (this.kpi.timeDimInput === 'M') {
        for (let i = 1; i <= 12; i++) {
          periodIndex = period + i.toString().padStart(2, '0')

          const raw =
            this.selectedKPIRecords[periodIndex].actual !== '' ? this.selectedKPIRecords[periodIndex].actual : this.selectedKPIRecords[periodIndex].forecast

          addValue(raw, true)

          // Quartalsabschluss
          if (i === 3 || i === 6 || i === 9 || i === 12) {
            const quarterIndex = period + (i / 3).toString()

            let result = ''

            if (this.kpi.aggregation === 'S') {
              result = quarterValue
            } else if (this.kpi.aggregation === 'M') {
              result = quarterCount > 0 ? quarterValue / quarterCount : ''
            } else if (this.kpi.aggregation === 'A') {
              result = quarterLast
            }

            this.selectedKPIRecords[quarterIndex].forecast = this.isValidNumber(result) ? this.formatNumber(result) : ''

            // Reset for next Quarter
            quarterValue = 0
            quarterCount = 0
            quarterLast = null
          }
        }

        // Jahreswert
        let yearResult = ''

        if (this.kpi.aggregation === 'S') {
          yearResult = yearValue
        } else if (this.kpi.aggregation === 'M') {
          yearResult = yearCount > 0 ? yearValue / yearCount : ''
        } else if (this.kpi.aggregation === 'A') {
          yearResult = yearLast
        }

        this.selectedKPIRecords[period].forecast =
          this.isValidNumber(yearResult) ? this.formatNumber(yearResult) : ''
      }

      // -----------------------------
      // QUARTERLY INPUT (Q)
      // -----------------------------
      if (this.kpi.timeDimInput === 'Q') {
        for (let i = 1; i <= 4; i++) {
          periodIndex = period + i.toString()

          const raw = this.selectedKPIRecords[periodIndex].actual !== '' ? this.selectedKPIRecords[periodIndex].actual : this.selectedKPIRecords[periodIndex].forecast

          addValue(raw, false)
        }

        let yearResult = ''

        if (this.kpi.aggregation === 'S') {
          yearResult = yearValue
        } else if (this.kpi.aggregation === 'M') {
          yearResult = yearCount > 0 ? yearValue / yearCount : ''
        } else if (this.kpi.aggregation === 'A') {
          yearResult = yearLast
        }

        this.selectedKPIRecords[period].forecast = this.isValidNumber(yearResult) ? this.formatNumber(yearResult) : ''
      }
    },

    calcValues (column, periodIndex) {
      if (this.selectedKPIRecords[periodIndex][column] === '-') return
      if (isNaN(parseFloat(this.selectedKPIRecords[periodIndex][column]))) {
        this.selectedKPIRecords[periodIndex][column] = ''
      }
      if (column === 'actual' && this.selectedKPIRecords[periodIndex].forecast !== '') {
        this.selectedKPIRecords[periodIndex].forecast = ''
      }

      const order = this.selectedKPIRecords.order
      if (this.kpi.timeDimInput === 'M') {
        const quarters = [3, 7, 11, 15]
        quarters.forEach(quarter => {
          let quarterValue = null
          let quarterValueForecast = null
          let validMonths = 0
          let validForecastMonths = 0
          for (let i = 1; i <= 3; i++) {
            if ((this.selectedKPIRecords[order[quarter - i]][column] !== '') || (column === 'actual' && this.selectedKPIRecords[order[quarter - i]].forecast !== '') || (column === 'forecast' && this.selectedKPIRecords[order[quarter - i]].actual !== '')) {
              validForecastMonths++
              if (column !== 'forecast' && this.selectedKPIRecords[order[quarter - i]][column] !== '') {
                validMonths++
              }
              if (this.kpiRecordMode === 'actual' && this.kpi.aggregation !== 'A') {
                if (quarterValueForecast) {
                  if (this.selectedKPIRecords[order[quarter - i]].actual !== '') {
                    quarterValueForecast = parseFloat(quarterValueForecast) + parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter - i]].actual))
                  } else {
                    quarterValueForecast = parseFloat(quarterValueForecast) + parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter - i]].forecast))
                  }
                } else {
                  if (this.selectedKPIRecords[order[quarter - i]].actual !== '') {
                    quarterValueForecast = parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter - i]].actual))
                  } else {
                    quarterValueForecast = parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter - i]].forecast))
                  }
                }
              }

              if (column !== 'forecast' && this.selectedKPIRecords[order[quarter - i]][column] !== '') {
                if (quarterValue) {
                  quarterValue = parseFloat(quarterValue) + parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter - i]][column]))
                } else {
                  quarterValue = parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter - i]][column]))
                }
              }
            }
          }

          if (column !== 'forecast') {
            if (quarterValue && this.kpi.aggregation === 'M') {
              quarterValue = (quarterValue / validMonths)
            } else if (this.kpi.aggregation === 'A') {
              for (let i = 1; i <= 3; i++) {
                quarterValue = this.unFormatNumber(this.selectedKPIRecords[order[(quarter - 4) + i]][column]) ? this.unFormatNumber(this.selectedKPIRecords[order[(quarter - 4) + i]][column]) : quarterValue
              }
            }
            this.isValidNumber(quarterValue) ? this.selectedKPIRecords[order[quarter]][column] = this.formatNumber(quarterValue) : this.selectedKPIRecords[order[quarter]][column] = ''
          }

          if (this.kpiRecordMode === 'actual') {
            if (this.kpi.aggregation === 'M' && quarterValueForecast) {
              quarterValueForecast = (quarterValueForecast / validForecastMonths)
            } else if (this.kpi.aggregation === 'A') {
              for (let i = 1; i <= 3; i++) {
                quarterValueForecast = this.unFormatNumber(this.selectedKPIRecords[order[(quarter - 4) + i]].actual) ? this.unFormatNumber(this.selectedKPIRecords[order[(quarter - 4) + i]].actual) : this.unFormatNumber(this.selectedKPIRecords[order[(quarter - 4) + i]].forecast) ? this.unFormatNumber(this.selectedKPIRecords[order[(quarter - 4) + i]].forecast) : quarterValueForecast
              }
            }
            this.isValidNumber(quarterValueForecast) ? this.selectedKPIRecords[order[quarter]].forecast = this.formatNumber(quarterValueForecast) : this.selectedKPIRecords[order[quarter]].forecast = ''
          }
        })
      }
      if (this.kpi.timeDimInput !== 'Y') {
        const quarters = this.kpi.timeDimInput === 'M' ? [3, 7, 11, 15] : [0, 1, 2, 3]
        let yearValue = ''
        let yearValueForecast = ''
        let validQuarters = 0
        let validForecastQuarters = 0
        quarters.forEach(quarter => {
          if (this.kpi.aggregation === 'A') {
            if (this.selectedKPIRecords[order[quarter]][column] !== '') {
              yearValue = parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter]][column]))
              yearValueForecast = parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter]].forecast))
            }
          } else {
            if (this.kpiRecordMode === 'actual') {
              if (yearValueForecast === '') {
                yearValueForecast = 0
                if (this.selectedKPIRecords[order[quarter]].forecast === '') {
                  yearValueForecast = parseFloat(yearValueForecast) + parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter]].actual))
                  validForecastQuarters++
                } else {
                  yearValueForecast = parseFloat(yearValueForecast) + parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter]].forecast))
                  validForecastQuarters++
                }
              } else {
                if (this.selectedKPIRecords[order[quarter]].forecast === '') {
                  if (this.selectedKPIRecords[order[quarter]].actual !== '') {
                    yearValueForecast = parseFloat(yearValueForecast) + parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter]].actual))
                    validForecastQuarters++
                  }
                } else {
                  yearValueForecast = parseFloat(yearValueForecast) + parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter]].forecast))
                  validForecastQuarters++
                }
              }
            }
            if (column !== 'forecast' && this.selectedKPIRecords[order[quarter]][column] !== '') {
              if (yearValue === '') {
                yearValue = parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter]][column]))
                validQuarters++
              } else {
                yearValue = parseFloat(yearValue) + parseFloat(this.unFormatNumber(this.selectedKPIRecords[order[quarter]][column]))
                validQuarters++
              }
            }
          }
          validForecastQuarters = validForecastQuarters + 0
        })

        // Year Value
        if (this.kpi.aggregation === 'M' && yearValue) {
          yearValue = (yearValue / validQuarters)
        }
        if (column === 'forecast' && validForecastQuarters > 0) yearValue = yearValueForecast

        if (this.kpi.timeDimInput === 'M') {
          this.selectedKPIRecords[order[16]][column] = this.formatNumber(yearValue)
        } else {
          this.isValidNumber(yearValue) ? this.selectedKPIRecords[order[4]][column] = this.formatNumber(yearValueForecast) : this.selectedKPIRecords[order[4]][column] = ''
          this.selectedKPIRecords[order[4]][column] = this.formatNumber(yearValue)
        }

        // Year Forecast
        if (yearValueForecast === 0) yearValueForecast = ''
        if (this.kpi.aggregation === 'M' && yearValueForecast) {
          yearValueForecast = (yearValueForecast / validForecastQuarters)
        }
        if (this.kpi.timeDimInput === 'M') {
          this.selectedKPIRecords[order[16]].forecast = this.formatNumber(yearValueForecast)
        } else {
          this.selectedKPIRecords[order[4]].forecast = this.formatNumber(yearValueForecast)
        }
      }
    },
    formatPeriod (period) {
      return formatUtils.formatPeriod(period)
    },
    formatNumber (number) {
      const userLocale = this.$store.state.user.settings?.language
      const decimals = this.kpi.decimals
      return formatUtils.formatNumber(userLocale, number, decimals)
    },
    formatInputNumber (number) {
      const userLocale = this.$store.state.user.settings?.language
      const decimals = this.kpi.decimals
      return formatUtils.formatInputNumber(userLocale, number, decimals)
    },
    unFormatNumber (number) {
      const userLocale = this.$store.state.user.settings?.language
      return formatUtils.unFormatNumber(userLocale, number)
    },
    isValidNumber (value) {
      return formatUtils.isValidNumber(value)
    },
    safeAdd (total, value) {
      const num = parseFloat(this.unFormatNumber(value))
      // Wenn value leer, null oder NaN ist → nichts addieren
      if (value === '' || value === null || isNaN(num)) {
        return total
      }
      return total + num
    }
  },

  setup () {
    const kpiRecordEditModal = ref(null)
    const closeModal = () => Modal.getInstance(kpiRecordEditModal.value)?.hide()
    return { kpiRecordEditModal, closeModal }
  }
}
</script>

<style scoped>
table {
  table-layout: fixed;
}
table thead th {
  text-align: end;
}
table input::-webkit-outer-spin-button,
table input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
table input[type=number] {
  appearance: textfield;
  -moz-appearance: textfield;
}
</style>
